import React, { useState, useEffect } from 'react';
import api from '../api';

// Shared pieces for the per-user Uptime and SSL pages. The API enforces who
// may do what; these only reflect the flags it returns on each monitor
// (is_default, owned_by_me, can_edit, can_check, delete_action).

// "Default" (shared) / "Mine" (private) badge for a monitor row. Admins
// viewing another user's list see "Private".
export function OwnerBadge({ monitor }) {
  if (monitor.is_default) {
    return <span className="status-badge status-neutral owner-badge" title="Shared default monitor">Default</span>;
  }
  if (monitor.owned_by_me) {
    return <span className="status-badge owner-badge owner-badge-mine" title="Your private monitor">Mine</span>;
  }
  return <span className="status-badge owner-badge owner-badge-other" title="Another user's private monitor (read-only)">Private</span>;
}

// Admin-only Owner filter: '' = own list, 'default' = all defaults,
// '<userId>' = that user's private monitors (read-only). Pages without
// defaults (IP Reputation) pass showDefaults={false}.
export function OwnerFilter({ user, value, onChange, showDefaults = true }) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/users')
      .then(res => setUsers(res.data.filter(u => u.id !== user.id)))
      .catch(err => console.error('Failed to load users for owner filter:', err));
  }, [user]);

  if (user?.role !== 'admin') return null;

  return (
    <select className="filter-select" value={value} onChange={e => onChange(e.target.value)}
            title="Owner" aria-label="Owner filter">
      <option value="">Owner: My list</option>
      {showDefaults && <option value="default">Owner: Defaults</option>}
      {users.map(u => (
        <option key={u.id} value={String(u.id)}>Owner: {u.name} ({u.email})</option>
      ))}
    </select>
  );
}

// Fetches the caller's hidden defaults for `type` ('uptime' | 'ssl').
export async function fetchHidden(type) {
  const res = await api.get('/' + type + '/hidden');
  return res.data;
}

// Modal listing hidden defaults (shown to users as "Deleted defaults") with a Restore action.
export function HiddenMonitorsModal({ type, items, describe, onClose, onRestored }) {
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const restore = async (id) => {
    setError('');
    setBusyId(id);
    try {
      await api.post('/' + type + '/' + id + '/restore');
      onRestored();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore monitor');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Deleted defaults</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {items.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              You have not deleted any default monitors.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                                         padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                    {describe(m)}
                  </span>
                  <button className="btn btn-secondary btn-sm" disabled={busyId === m.id} onClick={() => restore(m.id)}>
                    {busyId === m.id ? 'Restoring…' : 'Restore'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
